import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';

import { getSurfaceBorder, getThemeTokens } from '../../../theme/tokens';
import {
  EntityDependencyList,
  summarizeDependencyItems,
} from './EntityDependencyList';
import { EntityPanelFrame } from './EntityPanelFrame';
import { useEntityPageModel } from './useEntityPageModel';

const dependencyGraphRelations = [
  RELATION_PART_OF,
  RELATION_HAS_PART,
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_PROVIDES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
];

const DependencySection = ({
  description,
  items,
  title,
}: {
  description: string;
  items: ReturnType<typeof summarizeDependencyItems>;
  title: string;
}) => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'grid', gap: 0.5 }}>
        <Typography
          sx={{
            color: tokens.textPrimary,
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      </Box>

      {items.length ? (
        <EntityDependencyList items={items} />
      ) : (
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 14,
            lineHeight: 1.7,
          }}
        >
          No relationships are currently registered in this section.
        </Typography>
      )}
    </Box>
  );
};

export const EntityDependenciesRoute = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const model = useEntityPageModel();

  const upstreamItems = summarizeDependencyItems(model.dependencies.upstream);
  const downstreamItems = summarizeDependencyItems(
    model.dependencies.downstream,
  );

  if (!upstreamItems.length && !downstreamItems.length) {
    return (
      <EntityPanelFrame title="Dependencies">
        <Typography
          sx={{
            color: tokens.textMuted,
            fontSize: 14,
            lineHeight: 1.7,
          }}
        >
          No dependency relationships are currently registered for this entity.
        </Typography>
      </EntityPanelFrame>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 3,
        gridTemplateColumns: {
          lg: 'minmax(0, 1fr) minmax(0, 1fr)',
          xs: 'minmax(0, 1fr)',
        },
        alignItems: 'start',
      }}
    >
      <EntityPanelFrame title="Dependencies">
        <Stack
          spacing={3}
          sx={{
            '& > * + *': {
              borderTop: `1px solid ${getSurfaceBorder(theme)}`,
              paddingTop: 3,
            },
          }}
        >
          <DependencySection
            description="Things this entity belongs to, depends on, consumes, or is provided by."
            items={upstreamItems}
            title="Upstream relationships"
          />
          <DependencySection
            description="Things this entity provides, contains, supports, or that depend on it."
            items={downstreamItems}
            title="Downstream relationships"
          />
        </Stack>
      </EntityPanelFrame>

      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="Dependency graph"
        height={620}
        relations={dependencyGraphRelations}
        unidirectional={false}
      />
    </Box>
  );
};
